<template>
  <AisInstantSearch
    id="ais-InstantSearch"
    class="page-grid"
    :index-name="indexName"
    :middlewares="middlewares"
    :routing="routing"
    :search-client="getSearchClient()"
    :future="{
      preserveSharedStateOnUnmount: true,
    }"
  >
    <AisConfigure :hits-per-page.camel="12" />

    <div
      :class="{
        'gap-4': slots.filters,
      }"
      class="col-span-12 bg-white p-4 md:p-8 border-t-3 border-yellow-300 rounded-b-lg flex flex-col shadow-sm"
    >
      <SearchFilters :record-type="recordType" />

      <div class="max-md:hidden">
        <slot name="filters" />
      </div>

      <FilterAccordion
        v-if="slots.filters"
        class="md:hidden"
      >
        <slot name="filters" />
      </FilterAccordion>
    </div>

    <div class="col-span-12">
      <slot />
    </div>
  </AisInstantSearch>
</template>

<script lang="ts">
export default { name: 'SearchWrapper' }
</script>

<script setup lang="ts">
import { AisInstantSearch, AisConfigure } from 'vue-instantsearch/vue3/es'
import { connectMenu, connectPagination } from 'instantsearch.js/es/connectors'

import { onMounted, useSlots, watch } from 'vue'
import { history as historyRouter } from 'instantsearch.js/es/lib/routers'
import type { InstantSearch } from 'instantsearch.js/es/types/instantsearch'

import { createHistoryRecord } from 'swup'

import { getSearchClient, recordTypeToIndexName, stateToRoute, routeToState } from '~/lib/algoliaHelper'
import type { UiState, RouteState } from '~/lib/algoliaHelper'
import { RecordType } from '~/types/records/RecordType'

import SearchFilters from './SearchFilters.vue'
import FilterAccordion from './FilterAccordion.vue'
import LessonFilters from '~/constants/LessonFilters'

const slots = useSlots()

const props = defineProps<{
  recordType: RecordType
}>()

const indexName = recordTypeToIndexName(props.recordType)
const isLessonSearch = props.recordType === RecordType.Lesson
const filters = isLessonSearch ? LessonFilters : []

const routing = {
  router: historyRouter({
    push: (url:string) => createHistoryRecord(url),
  }),
  // https://www.algolia.com/doc/guides/building-search-ui/going-further/routing-urls/vue/#rewriting-urls-manually
  stateMapping: {
    stateToRoute: (uiState: UiState) => stateToRoute(indexName, uiState),
    routeToState: (routeState: RouteState) => routeToState(indexName, routeState),
  },
}
const middlewares = [
  ({ instantSearchInstance }: { instantSearchInstance: InstantSearch }): void => {
    // InstantSearch requires that a widget exists for an attribute before we set state for it,
    // so we create a virtual widget for each refinementList attribute that doesn't render anything
    // but ensures that the attribute exists in the InstantSearch internal store
    const virtualMenu = connectMenu(() => null)
    const virtualPagination = connectPagination(() => null)

    instantSearchInstance.addWidgets([
      virtualPagination({}),
      virtualMenu({ attribute: 'type' }),
      ...filters.map(({ facet }) => virtualMenu({ attribute: facet })),
    ])

    // We wait until InstantSearch has started before merging in the state from Craft
    watch(() => instantSearchInstance.started, () => {
      const currentState = instantSearchInstance.getUiState()[indexName]

      instantSearchInstance.setUiState({
        [indexName]: {
          ...currentState,
        },
      })
    })
  },
]

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search)
  const page = urlParams.get('page')

  if (page) {
    const element = document.getElementById('ais-InstantSearch')
    const motionQuery = matchMedia('(prefers-reduced-motion: reduce)')

    element?.scrollIntoView({
      behavior: motionQuery.matches ? 'auto' : 'smooth'
    })
  }
})
</script>
